import { Link, NavLink, Outlet } from 'react-router-dom';
import { HeartFilled } from '@ant-design/icons';
// Local brand logo. Vite handles the JPEG import at build time —
// the asset is fingerprinted, cache-busted, and served from /assets
// with a long max-age header. No external URL involved.
// The legal pages use the monochrome variant so the white header
// stays calm and the eye lands on the legal content rather than the
// brand color block.
import glassILogo from '../images/SaaS_Invoice_Logo_bw.jpeg';

// Shared layout for the public legal pages (Privacy, Terms,
// Refund, Contact). Renders a slim white header with the glass-i
// mark + section nav + auth CTAs, the page content as a centred
// card, and a horizontal footer that mirrors the auth-shell
// footer copy.
//
// LEGAL TEMPLATE NOTICE: footer links and contact details below
// reference business / legal placeholders. Have counsel review
// the page content before production and update any bracketed
// placeholders (registered office, legal entity, jurisdiction).

const NAV = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' },
  { to: '/refund-policy', label: 'Refund Policy' },
  { to: '/contact', label: 'Contact' }
];

function navLinkClass({ isActive }) {
  return `text-sm font-medium transition-colors ${
    isActive
      ? 'text-brand-600'
      : 'text-ink-600 hover:text-ink-900'
  }`;
}

export default function PublicLegalPageLayout() {
  const year = new Date().getFullYear();
  return (
    <div className="public-shell min-h-screen flex flex-col bg-surface-50">
      {/* Header */}
      <header className="bg-white border-b border-surface-200">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-3.5 flex items-center gap-4 flex-wrap">
          <Link
            to="/login"
            className="flex items-center shrink-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
            aria-label="Glass-i — go to login"
          >
            {/*
              Production-grade brand image:
              - Explicit width/height reserve layout so the navbar
                doesn't shift between request → paint → load (CLS=0).
                Tailwind h-9 still wins for rendered size; the
                attributes only provide the aspect-ratio hint.
              - fetchpriority="high" tells the browser to dispatch
                the request immediately — the logo is part of the
                LCP candidate set on these pages.
              - decoding="async" keeps the main thread responsive
                while the decoder runs off-thread.
              - loading="eager" overrides any layout-level lazy hint
                for above-the-fold content.
              - draggable=false avoids the ghost-drag UX that some
                users trigger by accident on a clickable logo.
            */}
            <img
              src={glassILogo}
              alt="Glass-i"
              width={160}
              height={36}
              fetchpriority="high"
              decoding="async"
              loading="eager"
              draggable={false}
              className="h-9 w-auto object-contain max-w-[160px] select-none"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-5 ml-4">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={navLinkClass}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 ml-auto">
            <Link
              to="/login"
              className="text-sm font-semibold text-ink-700 hover:text-ink-900 transition-colors"
            >
              Login
            </Link>
            <Link
              to="/signup"
              className="text-sm font-semibold rounded-lg bg-brand-500 hover:bg-brand-600 text-white px-3.5 py-1.5 transition-colors"
            >
              Start signup
            </Link>
          </div>
        </div>

        {/* Mobile nav row — wraps below the brand on narrow widths */}
        <nav className="md:hidden flex items-center gap-4 px-4 pb-3 overflow-x-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `text-sm font-medium whitespace-nowrap ${
                  isActive ? 'text-brand-600' : 'text-ink-600'
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
      </header>

      {/* Content */}
      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="mx-auto max-w-4xl bg-white rounded-2xl shadow-card border border-surface-200 px-5 sm:px-8 lg:px-10 py-7 sm:py-9">
          <Outlet />
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-surface-200 bg-white mt-auto">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-ink-500">
          <span className="font-semibold text-ink-700">Glass-i</span>
          <span>© {year}</span>
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="hover:text-ink-700 transition-colors"
            >
              {n.label}
            </Link>
          ))}
          <span className="md:ml-auto inline-flex items-center gap-1.5">
            Made with{' '}
            <HeartFilled
              className="text-rose-500 text-[11px]"
              aria-label="love"
            />{' '}
            for Indian glass industry
          </span>
        </div>
      </footer>
    </div>
  );
}
